// SkuGeneratorPage — /shizai/sku-generator
// Full-page SKU generator + recent-issued history for buyers/engineers.
// iter_138 · switched from families to templates as the filter/label source.
import { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '@/components/ui/select';
import { Hash, Sparkle, ClockCounterClockwise, CopySimple } from '@phosphor-icons/react';
import { toast } from 'sonner';
import { SkuWizard } from '@/components/SkuWizard';
import { skuMajorTitle } from '@/lib/skuDisplay';
import { formatDateTime } from '@/utils/datetime';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function SkuGeneratorPage() {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [issued, setIssued] = useState([]);
  const [filter, setFilter] = useState('all'); // template_id or 'all'
  const [templates, setTemplates] = useState([]);

  const load = useCallback(async () => {
    try {
      const params = filter === 'all' ? {} : { template_id: filter };
      const [r, t] = await Promise.all([
        axios.get(`${API}/sku/issued`, { params, withCredentials: true }),
        axios.get(`${API}/sku/templates`, { withCredentials: true }),
      ]);
      setIssued(r.data);
      setTemplates(t.data);
    } catch {
      // silent
    }
  }, [filter]);

  useEffect(() => { load(); }, [load]);

  const copySku = (sku) => {
    navigator.clipboard?.writeText(sku).then(() => toast.success('コピーしました'));
  };

  return (
    <div className="max-w-6xl mx-auto p-6 space-y-6" data-testid="sku-generator-page">
      {/* Hero */}
      <div className="rounded-lg bg-teal-700 p-8 text-white shadow-lg">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div>
            <div className="flex items-center gap-3">
              <Hash weight="duotone" size={40} className="opacity-90" />
              <h1 className="text-2xl font-semibold">品番自動採番</h1>
            </div>
            <p className="mt-2 text-sm opacity-90 max-w-xl">
              品番ルールを選ぶだけで、大分類・中分類・年号ごとに独立した連番を原子的に採番します。
            </p>
          </div>
          <Button
            size="lg"
            onClick={() => setDialogOpen(true)}
            className="bg-white text-blue-700 hover:bg-blue-50 shadow-md text-base"
            data-testid="sku-open-generator-btn"
          >
            <Sparkle weight="fill" size={18} />
            <span className="ml-2">新規発番</span>
          </Button>
        </div>
      </div>

      {/* Recent issued */}
      <Card className="p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <ClockCounterClockwise weight="duotone" className="text-indigo-500" size={22} />
            <h2 className="text-lg font-bold text-zinc-800">最近の発番履歴</h2>
          </div>
          <Select value={filter} onValueChange={setFilter}>
            <SelectTrigger className="w-56 h-9" data-testid="sku-history-filter">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">すべての品番ルール</SelectItem>
              {templates.map((t) => (
                <SelectItem key={t.id} value={t.id}>{t.name} · {skuMajorTitle(t)}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        {issued.length === 0 ? (
          <div className="text-center py-10 text-zinc-400 text-sm">まだ発番履歴はありません</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-xs text-zinc-500 border-b border-zinc-200">
                <tr>
                  <th className="text-left py-2 px-3">品番</th>
                  <th className="text-left py-2 px-3">品番ルール</th>
                  <th className="text-left py-2 px-3">バケット</th>
                  <th className="text-left py-2 px-3">発番者</th>
                  <th className="text-left py-2 px-3">日時</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {issued.slice(0, 100).map((it) => (
                  <tr key={it.id} className="border-b border-zinc-200 hover:bg-zinc-50" data-testid={`sku-issued-row-${it.sku}`}>
                    <td className="py-2 px-3 font-mono font-bold text-zinc-800">{it.sku}</td>
                    <td className="py-2 px-3 text-zinc-600">{it.template_name || it.template_code}</td>
                    <td className="py-2 px-3 text-xs text-zinc-500 font-mono">{it.bucket}</td>
                    <td className="py-2 px-3 text-xs text-zinc-500">{it.issued_by}</td>
                    <td className="py-2 px-3 text-xs text-zinc-500">
                      {formatDateTime(it.issued_at)}
                    </td>
                    <td className="py-2 px-3">
                      <Button size="sm" variant="ghost" onClick={() => copySku(it.sku)} data-testid={`sku-copy-${it.sku}`}>
                        <CopySimple size={14} />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <SkuWizard
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        defaultApp="shizai"
        onIssued={() => { load(); }}
      />
    </div>
  );
}
